<template>
  <div class="admin-table">
    <div class="page-header">
      <h1>{{ title }}</h1>
      <button class="add-btn" @click="openAddModal">+ 添加</button>
    </div>

    <div class="filter-bar">
      <button
        v-for="filter in filters"
        :key="filter.key"
        :class="['filter-btn', currentFilter === filter.key ? 'active' : '']"
        @click="setFilter(filter.key)"
      >
        {{ filter.label }}
      </button>
      <input
        v-model="searchQuery"
        type="text"
        class="search-input"
        placeholder="搜索..."
        @keyup.enter="handleSearch"
      />
    </div>

    <div class="content-body">
      <div class="loading" v-if="loading">加载中...</div>
      <div class="error" v-else-if="error">{{ error }}</div>

      <div class="table-container" v-else>
        <table class="data-table">
          <thead>
            <tr>
              <th v-for="col in columns" :key="col.key">{{ col.label }}</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in list" :key="item.id">
              <td v-for="col in columns" :key="col.key">
                <span v-if="col.type === 'tag'" :class="['tag', item[col.key]]">
                  {{ getTagLabel(col, item[col.key]) }}
                </span>
                <span v-else-if="col.type === 'date'">{{ formatDate(item[col.key]) }}</span>
                <label v-else-if="col.type === 'switch'" class="switch">
                  <input
                    type="checkbox"
                    :checked="item[col.key]"
                    @change="toggleSwitch(item, col.key)"
                    :disabled="!col.editable"
                  />
                  <span class="slider"></span>
                </label>
                <span v-else>{{ item[col.key] || '-' }}</span>
              </td>
              <td class="actions">
                <button class="edit-btn" @click="editItem(item)">编辑</button>
                <button class="delete-btn" @click="deleteItem(item.id)">删除</button>
              </td>
            </tr>
            <tr v-if="list.length === 0">
              <td :colspan="columns.length + 1" class="empty">暂无数据</td>
            </tr>
          </tbody>
        </table>

        <div class="pagination" v-if="totalPages > 1">
          <button :disabled="currentPage === 1" @click="changePage(currentPage - 1)">上一页</button>
          <span>{{ currentPage }} / {{ totalPages }}</span>
          <button :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">下一页</button>
        </div>
      </div>
    </div>

    <div class="modal" v-if="showModal">
      <div class="modal-content">
        <h2>{{ editId ? '编辑' : '添加' }}</h2>
        <form @submit.prevent="submitForm">
          <div class="form-group" v-for="field in formFields" :key="field.key">
            <label>{{ field.label }}</label>
            <input
              v-if="field.type === 'text' || field.type === 'number'"
              :type="field.type"
              v-model="form[field.key]"
              :required="field.required"
              :placeholder="field.placeholder"
            />
            <select v-else-if="field.type === 'select'" v-model="form[field.key]" :required="field.required">
              <option v-for="opt in field.options" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
            </select>
            <textarea
              v-else-if="field.type === 'textarea'"
              v-model="form[field.key]"
              :placeholder="field.placeholder"
            />
            <label v-else-if="field.type === 'switch'" class="switch-label">
              <input
                type="checkbox"
                v-model="form[field.key]"
              />
              <span class="slider"></span>
            </label>
          </div>
          <div class="form-actions">
            <button type="button" class="cancel-btn" @click="closeModal">取消</button>
            <button type="submit" class="submit-btn">提交</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { apiGet, apiPost, apiPut, apiDelete } from '@/utils/api'

export default {
  name: 'AdminTable',
  props: {
    apiEndpoint: { type: String, required: true },
    columns: { type: Array, required: true },
    filters: { type: Array, default: () => [] },
    formFields: { type: Array, required: true },
    title: { type: String, required: true }
  },
  data() {
    return {
      list: [],
      loading: true,
      error: '',
      searchQuery: '',
      currentFilter: 'all',
      currentPage: 1,
      pageSize: 10,
      total: 0,
      showModal: false,
      editId: null,
      form: {}
    }
  },
  computed: {
    totalPages() {
      return Math.ceil(this.total / this.pageSize)
    }
  },
  mounted() {
    this.fetchList()
    this.initForm()
  },
  methods: {
    async fetchList() {
      this.loading = true
      try {
        const params = {
          page: this.currentPage,
          pageSize: this.pageSize,
          filter: this.currentFilter !== 'all' ? this.currentFilter : undefined,
          search: this.searchQuery || undefined
        }
        const response = await apiGet(this.apiEndpoint, params)
        if (response.data.code === 200) {
          const data = response.data.data
          this.list = data.list || data || []
          this.total = data.total || this.list.length
        }
      } catch (err) {
        this.error = '获取数据失败'
      } finally {
        this.loading = false
      }
    },
    initForm() {
      const form = {}
      this.formFields.forEach(f => {
        form[f.key] = f.default || ''
      })
      this.form = form
    },
    getTagLabel(col, value) {
      if (!col.options || !Array.isArray(col.options)) return value
      const opt = col.options.find(o => o.value === value)
      return opt ? opt.label : value
    },
    formatDate(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('zh-CN')
    },
    handleSearch() {
      this.currentPage = 1
      this.fetchList()
    },
    setFilter(key) {
      this.currentFilter = key
      this.currentPage = 1
      this.fetchList()
    },
    changePage(page) {
      this.currentPage = page
      this.fetchList()
    },
    openAddModal() {
      this.editId = null
      this.initForm()
      this.showModal = true
    },
    editItem(item) {
      this.editId = item.id
      this.form = {}
      this.formFields.forEach(f => {
        this.form[f.key] = item[f.key] !== undefined ? item[f.key] : (f.default || '')
      })
      this.showModal = true
    },
    closeModal() {
      this.showModal = false
      this.editId = null
      this.initForm()
    },
    async submitForm() {
      try {
        if (this.editId) {
          await apiPut(`${this.apiEndpoint}/${this.editId}`, this.form)
        } else {
          await apiPost(this.apiEndpoint, this.form)
        }
        this.closeModal()
        this.fetchList()
      } catch (err) {
        alert('操作失败')
      }
    },
    async deleteItem(id) {
      if (!confirm('确定删除？')) return
      try {
        await apiDelete(`${this.apiEndpoint}/${id}`)
        this.fetchList()
      } catch (err) {
        alert('删除失败')
      }
    },
    toggleSwitch(item, key) {
      const newValue = !item[key]
      item[key] = newValue
      apiPut(this.apiEndpoint + '/' + item.id, { [key]: newValue }).catch(() => {
        alert('更新失败')
        this.fetchList()
      })
    }
  }
}
</script>

<style scoped>
.page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; }
.page-header h1 { font-size: 24px; color: #2c3e50; }
.add-btn { padding: 10px 20px; background: #3498db; color: white; border: none; border-radius: 5px; cursor: pointer; }
.filter-bar { display: flex; gap: 10px; margin-bottom: 20px; flex-wrap: wrap; }
.search-input { padding: 8px 15px; border: 1px solid #ddd; border-radius: 5px; flex: 1; min-width: 200px; }
.filter-btn { padding: 8px 20px; background: white; border: 1px solid #ddd; border-radius: 5px; cursor: pointer; }
.filter-btn.active { background: #3498db; color: white; border-color: #3498db; }
.content-body { background: white; padding: 20px; border-radius: 10px; box-shadow: 0 2px 10px rgba(0,0,0,0.1); }
.loading, .error, .empty { text-align: center; padding: 50px; color: #666; }
.table-container { overflow-x: auto; }
.data-table { width: 100%; border-collapse: collapse; }
.data-table th, .data-table td { padding: 12px 15px; text-align: left; border-bottom: 1px solid #eee; }
.data-table th { background: #f8f9fa; font-weight: bold; color: #2c3e50; }
.data-table tr:hover { background: #f5f5f5; }
.tag { display: inline-block; padding: 2px 8px; border-radius: 3px; font-size: 12px; background: #bdc3c7; color: white; }
.actions { display: flex; gap: 10px; }
.edit-btn, .delete-btn { padding: 6px 12px; border: none; border-radius: 5px; cursor: pointer; font-size: 12px; }
.edit-btn { background: #3498db; color: white; }
.delete-btn { background: #e74c3c; color: white; }
.pagination { display: flex; justify-content: center; align-items: center; gap: 15px; margin-top: 20px; }
.pagination button { padding: 8px 15px; background: white; border: 1px solid #ddd; border-radius: 5px; cursor: pointer; }
.pagination button:disabled { opacity: 0.5; cursor: not-allowed; }
.modal { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; justify-content: center; align-items: center; z-index: 1000; }
.modal-content { background: white; padding: 30px; border-radius: 10px; width: 500px; max-height: 90vh; overflow-y: auto; }
.modal-content h2 { margin: 0 0 20px; }
.form-group { margin-bottom: 15px; }
.form-group label { display: block; margin-bottom: 5px; color: #666; }
.form-group input, .form-group select, .form-group textarea { width: 100%; padding: 10px; border: 1px solid #ddd; border-radius: 5px; }
.form-group textarea { min-height: 80px; resize: vertical; }
.form-actions { display: flex; gap: 10px; justify-content: flex-end; margin-top: 20px; }
.cancel-btn, .submit-btn { padding: 10px 20px; border: none; border-radius: 5px; cursor: pointer; }
.cancel-btn { background: #ddd; }
.submit-btn { background: #3498db; color: white; }
.switch { position: relative; display: inline-block; width: 40px; height: 22px; }
.switch input { opacity: 0; width: 0; height: 0; }
.slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: #ccc; transition: 0.3s; border-radius: 22px; }
.slider:before { position: absolute; content: ""; height: 16px; width: 16px; left: 3px; bottom: 3px; background-color: white; transition: 0.3s; border-radius: 50%; }
.switch input:checked + .slider { background-color: #10b981; }
.switch input:checked + .slider:before { transform: translateX(18px); }
.switch input:disabled + .slider { opacity: 0.5; cursor: not-allowed; }
.switch-label { display: flex; align-items: center; gap: 10px; }
</style>
